import classNames from 'classnames';
import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import type { TooltipPosition } from './types';
import { getPosition } from './utils';

import styles from './Tooltip.module.scss';

const Tooltip: FC<Required<PropsWithChildren<{ message: string }>>> = ({
  children,
  message,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<TooltipPosition>({
    left: 0,
    top: 0,
    direction: 'up',
    offset: 0,
  });

  const show = useCallback(() => setIsVisible(true), []);
  const hide = useCallback(() => setIsVisible(false), []);

  const tooltipRect = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (isVisible && tooltipRect.current) {
      const position = getPosition(tooltipRect.current);
      setPosition(position);
    }
  }, [isVisible]);

  return (
    <span
      onMouseOver={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      className={styles['tooltip-wrapper']}
      ref={tooltipRect}
    >
      {children}
      {isVisible &&
        createPortal(
          <div
            className={classNames(
              styles['tooltip-container'],
              styles[position.direction],
            )}
            style={{
              left: position.left,
              top: position.top,
              transform: `translateX(${position.offset}px)`,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="13"
              viewBox="0 0 15 13"
              fill="currentColor"
              className={styles.tile}
              style={{
                transform: `translateX(${position.offset * -1}px) rotate(${position.direction === 'up' ? 0 : 180}deg)`,
              }}
            >
              <title>tile</title>
              <path
                d="M9.20675 12.0391C8.42691 13.3154 6.57333 13.3154 5.79349 12.0391L0.296509 3.04279C-0.517811 1.71008 0.441335 0 2.00314 0H12.9971C14.5589 0 15.5181 1.71008 14.7037 3.04279L9.20675 12.0391Z"
                fill="currentColor"
              />
            </svg>
            <div className={styles.message}>{message}</div>
          </div>,
          document.body,
        )}
    </span>
  );
};

export default Tooltip;
